import React, { useState, useEffect } from 'react';
import {
  Card, Button, Space, Tag, Steps, Radio, Input, Select, message, Result, Descriptions, Spin
} from 'antd';
import {
  ShopOutlined, FileTextOutlined, EditOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const { TextArea } = Input;

function ApiOrder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [platforms, setPlatforms] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [accounts, setAccounts] = useState('');
  const [team, setTeam] = useState('');
  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/platforms').then(res => {
      if (res.code === 200) setPlatforms(res.data || []);
    }).catch(() => {});
  }, []);

  // 加载策略列表
  useEffect(() => {
    api.get('/api-strategies').then(res => {
      if (res.code === 200) setStrategies(res.data || []);
    }).catch(() => {});
  }, []);

  // 按平台过滤策略
  const filteredStrategies = strategies.filter(s =>
    s.status === 1 && (!selectedPlatform || s.platform === selectedPlatform)
  );

  const handleSelectStrategy = (strategy) => {
    setSelectedStrategy(strategy);
    setTeam(strategy.team || '');
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!selectedStrategy) {
      message.warning('请先选择策略');
      return;
    }

    const accountList = accounts
      .split('\n')
      .map(a => a.trim())
      .filter(a => a.length > 0);

    if (accountList.length === 0) {
      message.warning('请输入至少一个账号');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/api-orders', {
        strategy_id: selectedStrategy.id,
        accounts: accountList,
        team: team || null,
        remark: remark || null
      });

      if (res.code === 200) {
        setResult(res.data);
        setStep(3);
      } else {
        message.error(res.message || '下单失败');
      }
    } catch (err) {
      message.error('下单失败');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setStep(0);
    setSelectedPlatform('');
    setSelectedStrategy(null);
    setAccounts('');
    setTeam('');
    setRemark('');
    setResult(null);
  };

  const getWorkflowDisplay = (steps) => {
    if (!steps || steps.length === 0) return '-';
    return steps.map((s, i) => `${i + 1}.${s}`).join(' → ');
  };

  return (
    <div>
      <Card title="API下单">
        <Steps
          current={step}
          style={{ marginBottom: 32 }}
          items={[
            { title: '选择平台', icon: <ShopOutlined /> },
            { title: '选择策略', icon: <FileTextOutlined /> },
            { title: '填写订单', icon: <EditOutlined /> },
            { title: '完成', icon: <CheckCircleOutlined /> }
          ]}
        />

        {/* 步骤1: 选择平台 */}
        {step === 0 && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <h3 style={{ marginBottom: 24 }}>选择平台</h3>
            <Radio.Group
              value={selectedPlatform}
              onChange={e => setSelectedPlatform(e.target.value)}
              size="large"
              buttonStyle="solid"
            >
              <Radio.Button value="">全部平台</Radio.Button>
              {platforms.map(p => (
                <Radio.Button key={p.id} value={p.name}>{p.name}</Radio.Button>
              ))}
            </Radio.Group>
            <div style={{ marginTop: 32 }}>
              <Button type="primary" size="large" onClick={() => setStep(1)}>
                下一步：选择策略
              </Button>
            </div>
          </div>
        )}

        {/* 步骤2: 选择策略 */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Button onClick={() => setStep(0)}>← 返回选择平台</Button>
              <span style={{ marginLeft: 16, color: '#666' }}>
                当前平台: {selectedPlatform || '全部'}
              </span>
            </div>

            {filteredStrategies.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                暂无可用策略，请联系管理员配置
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                {filteredStrategies.map(strategy => (
                  <Card
                    key={strategy.id}
                    hoverable
                    onClick={() => handleSelectStrategy(strategy)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Meta
                      title={
                        <Space>
                          <span>{strategy.name}</span>
                          <Tag color="blue">{strategy.platform}</Tag>
                        </Space>
                      }
                      description={
                        <div style={{ marginTop: 8 }}>
                          {strategy.growth_method && (
                            <div><span style={{ color: '#999' }}>涨粉方式:</span> {strategy.growth_method}</div>
                          )}
                          {strategy.team && (
                            <div><span style={{ color: '#999' }}>默认小组:</span> {strategy.team}</div>
                          )}
                          {strategy.price && (
                            <div><span style={{ color: '#999' }}>单价:</span> ¥{strategy.price}</div>
                          )}
                          <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                            流程: {getWorkflowDisplay(strategy.workflow_steps)}
                          </div>
                          <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
                            API操作: {(strategy.apis || []).length}个
                          </div>
                        </div>
                      }
                    />
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 步骤3: 填写订单 */}
        {step === 2 && selectedStrategy && (
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <Button onClick={() => setStep(1)} style={{ marginBottom: 16 }}>← 返回选择策略</Button>

            <Card style={{ marginBottom: 24 }}>
              <Descriptions column={2} size="small" title="已选策略">
                <Descriptions.Item label="策略名称">{selectedStrategy.name}</Descriptions.Item>
                <Descriptions.Item label="平台">{selectedStrategy.platform}</Descriptions.Item>
                <Descriptions.Item label="涨粉方式">{selectedStrategy.growth_method || '-'}</Descriptions.Item>
                <Descriptions.Item label="小组">{selectedStrategy.team || '-'}</Descriptions.Item>
                <Descriptions.Item label="单价">{selectedStrategy.price ? `¥${selectedStrategy.price}` : '-'}</Descriptions.Item>
                <Descriptions.Item label="流程步骤">
                  {(selectedStrategy.workflow_steps || []).length}步
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                账号（一行一个）
              </div>
              <TextArea
                rows={6}
                value={accounts}
                onChange={e => setAccounts(e.target.value)}
                placeholder="user12345&#10;abcd67890&#10;xyz_live"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                小组（选填，覆盖策略默认）
              </div>
              <Select
                value={team || undefined}
                onChange={v => setTeam(v || '')}
                allowClear
                placeholder="使用策略默认小组"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                备注
              </div>
              <Input
                value={remark}
                onChange={e => setRemark(e.target.value)}
                placeholder="备注信息（选填）"
              />
            </div>

            <div style={{ textAlign: 'center' }}>
              <Space>
                <Button onClick={resetAll}>取消</Button>
                <Button type="primary" loading={submitting} onClick={handleSubmit}>
                  提交下单
                </Button>
              </Space>
            </div>
          </div>
        )}

        {/* 步骤4: 完成 */}
        {step === 3 && result && (
          <div style={{ maxWidth: 500, margin: '0 auto' }}>
            <Result
              status={result.failed > 0 ? 'warning' : 'success'}
              title={`下单完成`}
              subTitle={
                <div>
                  <div>创建成功: {result.created} 条</div>
                  <div>API提交成功: {result.submitted} 条</div>
                  <div>失败: {result.failed} 条</div>
                  <div>跳过(重复): {result.skipped} 条</div>
                </div>
              }
            />

            {result.details && result.details.length > 0 && (
              <Card title="详细结果" size="small" style={{ marginBottom: 24 }}>
                {result.details.map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span>{d.account_name}</span>
                    <Space>
                      {d.status === 'submitted' && <Tag color="green">已提交</Tag>}
                      {d.status === 'pending' && <Tag color="blue">待处理</Tag>}
                      {d.status === 'failed' && <Tag color="red">失败</Tag>}
                      {d.status === 'skipped' && <Tag color="orange">跳过</Tag>}
                      {d.error && <span style={{ color: '#999', fontSize: 12 }}>{d.error}</span>}
                    </Space>
                  </div>
                ))}
              </Card>
            )}

            <div style={{ textAlign: 'center' }}>
              <Space>
                <Button onClick={resetAll}>继续下单</Button>
                <Button type="primary" onClick={() => navigate('/api-orders')}>查看对接列表</Button>
              </Space>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default ApiOrder;
